/**
 * AuthCard — autenticazione Phase 5 (IMPL-P5-007).
 *
 * Thin shadcn `<Card>` composition for auth screens. Provides title +
 * optional description in the header, freeform `children` in the body, and
 * an optional `footer` slot rendered inside `<CardFooter>`.
 *
 * The title is rendered as `<h1>` so each auth page has a unique top-level
 * heading (a11y: "focus moves to <h1>" acceptance criterion).
 */
import type { ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';

interface AuthCardProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthCard({ title, description, children, footer }: AuthCardProps) {
  return (
    <Card>
      <CardHeader className="space-y-2">
        <h1 className="text-2xl font-semibold leading-none tracking-tight" tabIndex={-1}>
          {title}
        </h1>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
      {footer ? (
        <CardFooter className="flex flex-col items-start gap-2 text-sm">{footer}</CardFooter>
      ) : null}
    </Card>
  );
}
